import type { ShopProduct } from '../types';

/**
 * The Be You menu's data shapes, and Phase 6's merge of the salon's
 * Special Services into it. A plain module (no JSX) so it can be tested
 * on its own; BeYouView renders it.
 */

export interface BeYouCategory {
  id: string;
  name: string;
  sub: string;
  count: number;
  soon?: boolean;
  blurb?: string;
  from?: number;
  perMo?: boolean;
  /**
   * Hidden categories don't appear in the tab bar, the "All" tab, or
   * search — they're only reachable via deep link (?category=<id>).
   * Used for member-only collections (Be You Skin Club et al.) that
   * shouldn't be public but should be linkable from a member portal.
   */
  hidden?: boolean;
}

export interface BeYouTier {
  id: string | number;
  label: string;
  price: number;
  save?: number;
  unit?: string;
  // GHL calendar ID this tier books against. Live-mapped from the Be You
  // location's catalog 2026-05-18 via `npx tsx -e "list services"` against
  // pit-* token. Each tier is its own GHL calendar (the merchant set this
  // up that way) so the tier IS the bookable unit. Null when the tier
  // exists in the display catalog but no matching GHL calendar was found
  // — those cards still render but Continue stays disabled.
  ghlCalendarId?: string;
  /** Phase 6 — a Special Service option, bought with no appointment. */
  productId?: string;
}

export interface BeYouArea {
  id: string;
  label: string;
  tiers: BeYouTier[];
}

export interface BeYouService {
  id: string;
  name: string;
  desc: string;
  duration: number;
  variant: 'sessions' | 'area_then_sessions' | 'areas_count' | 'flat' | 'subscription' | 'packages' | 'consult';
  tiers?: BeYouTier[];
  areas?: BeYouArea[];
  price?: number;
  unitLabel?: string;
  badge?: string;
  addon?: { label: string; unit: number };
  // For 'flat' / 'consult' services that map to a single GHL calendar.
  ghlCalendarId?: string;
  /** Phase 6 — a single-price Special Service, bought with no appointment. */
  productId?: string;
  /** Replaces the duration in the meta line (a purchase has none). */
  metaLabel?: string;
}

export interface Catalog {
  categories: BeYouCategory[];
  services: Record<string, BeYouService[]>;
}

/**
 * Phase 6 — the menu with the salon's Special Services (bootstrap `shop`).
 * GLP-1 becomes purchasable cards at the store's prices, one per program
 * with its options as tiers, replacing the old calendar-booked cards; the
 * prescriptions get their own tab. Without a shop the static menu is
 * returned untouched.
 */
export function buildCatalog(base: Catalog, shop: ShopProduct[] | undefined): Catalog {
  if (!shop || shop.length === 0) return base;
  const listed = shop.filter((p) => !p.hidden);
  const meta = 'Online · no appointment needed';
  const groups = new Map<string, ShopProduct[]>();
  for (const p of listed.filter((x) => x.category === 'glp1')) {
    const k = p.groupName ?? p.name;
    groups.set(k, [...(groups.get(k) ?? []), p]);
  }
  const glp: BeYouService[] = [...groups.entries()].map(([group, opts]) => ({
    id: `shop-${group}`,
    name: `${group} GLP-1`,
    desc: opts[0]?.description ?? '',
    duration: 0,
    metaLabel: meta,
    variant: 'packages',
    tiers: opts.map((o) => ({ id: o.id, label: o.optionLabel ?? o.name, price: o.priceCents / 100, productId: o.id })),
  }));
  const rx: BeYouService[] = listed
    .filter((p) => p.category === 'rx')
    .map((p) => ({
      id: `shop-${p.id}`,
      name: p.name,
      desc: p.description ?? '',
      duration: 0,
      metaLabel: meta,
      variant: 'flat',
      price: p.priceCents / 100,
      productId: p.id,
    }));
  const minPrice = (xs: BeYouService[]) =>
    Math.min(...xs.flatMap((x) => (x.tiers ? x.tiers.map((t) => t.price) : [x.price ?? Infinity])));
  const categories = base.categories.map((c) =>
    c.id === 'weight' && glp.length > 0 ? { ...c, count: glp.length, from: minPrice(glp), perMo: false } : c,
  );
  if (rx.length > 0) {
    categories.push({
      id: 'rx',
      name: 'Prescriptions & Wellness',
      sub: 'Prescription treatments with a virtual medical evaluation — delivered to your home.',
      count: rx.length,
      from: minPrice(rx),
      blurb: 'Prescriptions & Wellness',
    });
  }
  return {
    categories,
    services: { ...base.services, ...(glp.length > 0 ? { weight: glp } : {}), ...(rx.length > 0 ? { rx } : {}) },
  };
}
